import {
  type ConvictionChartSnapshotInput,
  mergeConvictionChartSnapshots,
  type QueryClient,
  type QueryKey,
  readMarketChartSnapshot,
} from '@morfi/backend-sdk-v2/client';
import { mapMarketChartRows, type SparseChartEntry } from '@/lib/chart/map-market-chart-rows';
import { pickChartLeafConverter } from '@/lib/chart/sdk-mapping';
import type { ChartInterval } from './types';

export interface MarketChartOpening {
  readonly property?: string;
  readonly marketPubkey: string;
  readonly anchorMinuteMs: number;
  readonly allSnapshotToMs?: number;
  readonly chartType?: 'line' | 'ohlcv';
  readonly interval: ChartInterval;
  readonly convictionQueries?: readonly QueryKey[];
  /** Client intent reads the existing SDK hook result without storing a second snapshot. */
  readonly readConvictionSnapshot?: () => ConvictionChartSnapshotInput | null | undefined;
  readonly queries: readonly {
    readonly outcomeIndex: number;
    readonly queryKey: QueryKey;
    readonly from?: string;
    readonly to?: string;
  }[];
  readonly terminalQueries?: MarketChartOpening['queries'];
}

/** Keep the server frame for presentation while measured density acquires its own wire snapshot. */
export function readConvictionChartOpening(
  client: Pick<QueryClient, 'getQueryData'>,
  opening: MarketChartOpening | undefined,
  marketPubkey: string,
): ConvictionChartSnapshotInput | null {
  if (opening?.marketPubkey !== marketPubkey || opening.property !== 'twar') return null;
  if (opening.readConvictionSnapshot !== undefined) return opening.readConvictionSnapshot() ?? null;
  const pages = (opening.convictionQueries ?? []).map((key) =>
    client.getQueryData<ConvictionChartSnapshotInput>(key),
  );
  if (pages.some((page) => page === undefined)) return null;
  return mergeConvictionChartSnapshots(pages.filter((page) => page !== undefined));
}

/** Read the exact hydrated identities once, before the realtime publishers mount. */
export function readMarketChartOpening(
  client: Pick<QueryClient, 'getQueryData'>,
  opening: MarketChartOpening | undefined,
  marketPubkey: string,
  section: 'main' | 'terminal' = 'main',
  property: string = opening?.property ?? 'twar',
): Record<number, SparseChartEntry> {
  if (opening?.marketPubkey !== marketPubkey) return {};
  if ((opening.property ?? 'twar') !== property) return {};
  const convert = pickChartLeafConverter(property);
  const entries: Record<number, SparseChartEntry> = {};
  const queries = section === 'terminal' ? (opening.terminalQueries ?? []) : opening.queries;
  for (const query of queries) {
    const envelope = readMarketChartSnapshot(client, query.queryKey);
    if (envelope === undefined) continue;
    entries[query.outcomeIndex] = mapMarketChartRows(
      envelope.data,
      section === 'terminal' ? 'line' : (opening.chartType ?? 'line'),
      convert,
      {
        ...(envelope.virtualQuote === undefined ? {} : { virtualQuote: envelope.virtualQuote }),
        ...(envelope.leftEdgeValue === undefined
          ? {}
          : { leftEdgeValue: convert(envelope.leftEdgeValue) }),
        ...(envelope.bucketSeconds === undefined ? {} : { bucketSeconds: envelope.bucketSeconds }),
        ...(envelope.rangeStart === undefined ? {} : { requestedFrom: envelope.rangeStart }),
        ...(envelope.rangeEnd === undefined ? {} : { requestedTo: envelope.rangeEnd }),
        ...(envelope.closedBefore === undefined ? {} : { closedBefore: envelope.closedBefore }),
      },
    );
  }
  return entries;
}
